import { useEffect, useMemo, useState } from 'react';
import { requestPost } from '../util/request';

interface TemporaryUrlResponseData {
  success?: boolean;
  data?: {
    url: string;
    expiresAt?: string;
    inputUrl?: string;
    duration?: number;
    aliCdnUrl?: string;
  };
  code?: number;
  message?: string;
}

interface CachedEntry {
  url: string;
  expiresAt: number;
}

const CACHE_STORAGE_KEY = 's3TemporaryUrlCache';
const FIVE_DAYS_SECONDS = 5 * 24 * 60 * 60;

function loadCache(): Record<string, CachedEntry> {
  try {
    const cached = localStorage.getItem(CACHE_STORAGE_KEY);
    return cached ? (JSON.parse(cached) as Record<string, CachedEntry>) : {};
  } catch {
    return {};
  }
}

function saveCache(cache: Record<string, CachedEntry>) {
  try {
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // ignore storage errors
  }
}

function isS3Url(url: string): boolean {
  if (!/^https?:\/\//i.test(url)) return false;
  try {
    const parsed = new URL(url);
    return /s3.*\.amazonaws\.com$/i.test(parsed.hostname) || /\.s3\./i.test(parsed.hostname);
  } catch {
    return false;
  }
}

async function getTemporaryUrlFromUrl(originalUrl: string, durationSeconds = FIVE_DAYS_SECONDS): Promise<string | null> {
  const payload = { url: originalUrl, durationSeconds };
  const res = await requestPost<TemporaryUrlResponseData>('/kanvas-api/api/v1/image/temporary-url-from-url', payload, true);
  if (!res) return null;
  // API may return either {success, data} or a common {code, data}
  const ok = (typeof res.success === 'boolean' ? res.success : undefined) ?? (res.code === 200);
  if (ok && res.data?.aliCdnUrl) {
    return res.data.aliCdnUrl;
  }
  return null;
}

export function useResolvedCoverImage(coverImage?: string | null): string | null {
  const [resolved, setResolved] = useState<string | null>(null);
  const temporaryBase = process.env.REACT_APP_PROXY_TARGET ?? '';

  const needsAsync = useMemo(() => {
    if (!coverImage) return false;
    if (/^https?:\/\//i.test(coverImage)) {
      return isS3Url(coverImage);
    }
    return false;
  }, [coverImage]);

  useEffect(() => {
    if (!coverImage) {
      setResolved(null);
      return;
    }

    // Absolute non-S3 URL -> use as-is
/*    if (/^https?:\/\//i.test(coverImage) && !isS3Url(coverImage)) {
      setResolved(coverImage);
      return;
    }*/

    // Legacy relative path -> prefix proxy base
    if (!/^https?:\/\//i.test(coverImage)) {
      setResolved(`${temporaryBase}${coverImage}`);
      return;
    }

    // S3 URL -> try cache then fetch temporary URL
    let cancelled = false;
    (async () => {
      const cache = loadCache();
      const cached = cache[coverImage];
      const now = Date.now();
/*      if (cached && cached.expiresAt > now + 60 * 1000) {
        // use cached if >60s remaining to avoid near-expiry urls
        if (!cancelled) setResolved(cached.url);
        return;
      }*/
      const tmp = await getTemporaryUrlFromUrl(coverImage);
      if (tmp) {
        // estimate expiry: now + FIVE_DAYS_SECONDS; if server gave expiresAt we could parse, but keep simple
        const entry: CachedEntry = { url: tmp, expiresAt: now + FIVE_DAYS_SECONDS * 1000 };
        cache[coverImage] = entry;
        saveCache(cache);
        if (!cancelled) setResolved(tmp);
      } else {
        // fallback to original (may fail to load)
        if (!cancelled) setResolved(coverImage);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coverImage, temporaryBase]);

  return resolved;
}


